import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STAV_POPIS, StavBadge } from "@/components/zakazka/StavBadge";
import { ZakazkaEditDialog } from "@/components/zakazka/ZakazkaEditDialog";
import { DoplnitAaDialog } from "@/components/zakazka/DoplnitAaDialog";
import { druhZakazky } from "@/lib/druhCisla";
import { adresaZoznamu } from "@/lib/zoznamZakaziek";
import type { ZakazkaDetail } from "@/api/schemas";

export interface ZakazkaHeaderProps {
  zakazka: ZakazkaDetail;
}

export function ZakazkaHeader({ zakazka }: ZakazkaHeaderProps): JSX.Element {
  const [editOpen, setEditOpen] = useState(false);
  const [aaOpen, setAaOpen] = useState(false);
  // Výpočet (V) čaká na AA, malá zákazka (Z) ho nikdy nedostane (KOLO 200)
  const druh = druhZakazky(zakazka);

  // Sekundárne číslo výpočtu (V) — keď zákazka prešla do výroby (AA)
  const vypocetVedla =
    zakazka.vypocet_cislo && zakazka.vypocet_cislo !== zakazka.cislo_zakazky
      ? zakazka.vypocet_cislo
      : null;

  return (
    // Jeden 36 px pás namiesto štyroch riadkov (140 px). Číslo zákazky sa
    // počas práce nemení, takže ho netreba mať ako `text-3xl` nadpis —
    // uvoľnené miesto ide toolbaru a kusovníku.
    <div className="group flex h-9 items-center gap-2 overflow-hidden">
      <Button
        variant="ghost"
        size="icon"
        asChild
        className="h-8 w-8 shrink-0"
        aria-label="Späť na zoznam zákaziek"
        title="Späť na zoznam zákaziek"
      >
        {/* Späť na filter, z ktorého user prišiel (KOLO 171) */}
        <Link to={adresaZoznamu()}>
          <ArrowLeft className="h-4 w-4" />
        </Link>
      </Button>

      <h1 className="shrink-0 font-mono text-xl leading-none">
        {zakazka.cislo_zakazky}
      </h1>
      {vypocetVedla ? (
        <span
          className="shrink-0 font-mono text-sm text-muted-foreground"
          title="Číslo výpočtu"
        >
          {vypocetVedla}
        </span>
      ) : null}

      {druh === "vypocet" ? (
        <Button
          size="sm"
          variant="outline"
          className="h-7 shrink-0 px-2 text-xs"
          onClick={() => setAaOpen(true)}
          title="Z výpočtu bude zákazka — V ostane ako číslo výpočtu"
        >
          Doplniť číslo AA
        </Button>
      ) : druh === "mala" ? (
        <span
          className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
          title="Tandem ju nevyrába — číslo Z je trvalé, AA nedostane"
        >
          malá zákazka bez AA
        </span>
      ) : null}

      {zakazka.nazov ? (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="truncate text-sm text-muted-foreground">
            {zakazka.nazov}
          </span>
        </>
      ) : null}
      {/* Klient a termín odišli (KOLO 162) — user ich nepoužíva */}

      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={() => setEditOpen(true)}
        aria-label="Upraviť údaje zákazky"
        title="Upraviť číslo, názov…"
      >
        <Pencil className="h-4 w-4" />
      </Button>

      <div
        className="ml-auto flex shrink-0 items-center"
        title={STAV_POPIS}
      >
        <StavBadge stav={zakazka.stav} />
      </div>

      <DoplnitAaDialog zakazka={zakazka} open={aaOpen} onOpenChange={setAaOpen} />
      <ZakazkaEditDialog
        zakazkaId={editOpen ? zakazka.id : null}
        onOpenChange={(open) => {
          if (!open) setEditOpen(false);
        }}
      />
    </div>
  );
}
